import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BasePageComponent } from '../../../../../../../../_shared/components/page/base-page.component';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CompliancesLibraryService } from '../../../../../services/compliances-library.service';
import { AssetType } from '../../../../../../../../asset-management/_shared/enums/asset-type.enum';
import { AssignedAssets, GetAssignedAssetsRequest } from '../../../../../models/compliances.model';
import { ErrorHandlerService } from '../../../../../../../../_shared/services/error-handler.service';
import { ComplianceTaskType, TableColumn } from '../../../../../../compliance-overview/models/compliance-item.model';
import { ComplianceTasksTableComponent } from '../../../../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ConfirmationModalComponent } from '../../../../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { EditFrequencyModalComponent } from './edit-frequency-modal/edit-frequency-modal.component';
import { TranslocoService } from '@jsverse/transloco';
import { translateComplianceFrequency, translateRoutineTypeName } from '../../../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-assigned-assets',
  standalone: true,
  imports: [
    CommonModule,
    ComplianceTasksTableComponent
  ],
  templateUrl: './assigned-assets.component.html',
  styleUrl: './assigned-assets.component.scss'
})
export class AssignedAssetsComponent extends BasePageComponent implements OnInit, OnChanges, OnDestroy {
  private complianceLibraryService: CompliancesLibraryService = inject(CompliancesLibraryService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService: NgbModal = inject(NgbModal);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);
  private cancellation = new Subject<void>();

  @Input() complianceId: number = 0;
  @Input() complianceName: string = '';
  @Input() complianceType: string = '';
  @Input() searchTerm: string = '';
  @Input() assetType: AssetType | null = null;

  get tableTaskType(): ComplianceTaskType {
    return this.complianceType?.toLowerCase() === 'routine' ? 'routine' : 'periodic';
  }

  assignedAssets: AssignedAssets[] = [];
  isLoading: boolean = true;
  isLoadingMore: boolean = false;
  hasError: boolean = false;

  page: number = 1;
  pageSize: number = 15;
  totalPages: number = 0;
  totalCount: number = 0;

  get columns(): TableColumn[] {
    return [
      { header: this.transloco.translate('compliance.library.assignedAssets.columns.asset'), value: 'name', width: 'col-2', type: 'text', truncate: true, title: true, bold: true },
      { header: this.transloco.translate('compliance.library.assignedAssets.columns.type'), value: 'type', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.library.assignedAssets.columns.makeModel'), value: 'makeModel', width: 'col', type: 'nullable-text' },
      { header: this.complianceTypeColumnHeader, value: 'complianceFrequencyLabel', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.library.assignedAssets.columns.status'), value: 'status', width: 'col', type: 'status' },
      { header: this.transloco.translate('compliance.library.assignedAssets.columns.action'), value: 'action', width: 'col-1', type: 'action' },
    ];
  }

  private get complianceTypeColumnHeader(): string {
    return this.complianceType?.toLowerCase() === 'routine'
      ? this.transloco.translate('compliance.library.assignedAssets.columns.routineType')
      : this.transloco.translate('compliance.library.assignedAssets.columns.frequency');
  }

  override ngOnInit(): void {
    void this.loadAssignedAssetsAsync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const filterChanges = [changes['searchTerm'], changes['assetType']].filter(Boolean);

    if (!filterChanges.length || filterChanges.every(change => change.firstChange)) {
      return;
    }

    this.resetAndLoad();
  }

  override ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.page >= this.totalPages) return;
    this.page++;
    void this.loadAssignedAssetsAsync();
  }

  onHistoryClick(item: AssignedAssets): void {
    this.router.navigate(['/asset-management/compliance-library/compliance-details/assigned-assets/history'], {
      queryParams: {
        assetId: item.id,
        assetName: item.name,
        complianceId: this.complianceId,
        complianceName: this.complianceName,
        complianceType: this.complianceType
      }
    });
  }

  onEditRequirementsClick(item: AssignedAssets): void {
    this.router.navigate(['/asset-management/compliance-library/compliance-details/assigned-assets/edit-requirements'], {
      queryParams: {
        assetId: item.id,
        assetName: item.name,
        complianceId: this.complianceId,
        complianceName: this.complianceName,
        complianceType: this.complianceType
      }
    });
  }

  onEditFrequencyClick(item: AssignedAssets): void {
    const modalRef = this.modalService.open(EditFrequencyModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'edit-frequency-modal-window'
    });
    modalRef.componentInstance.asset = item;
    modalRef.componentInstance.complianceId = this.complianceId;
    modalRef.closed.subscribe((saved: boolean) => {
      if (saved) {
        this.resetAndLoad();
      }
    });
  }

  onDeleteClick(item: AssignedAssets): void {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      windowClass: 'confirmation-modal__content'
    });

    modalRef.componentInstance.data = {
      title: this.transloco.translate('compliance.library.assignedAssets.confirmations.delete.title'),
      message: this.transloco.translate('compliance.library.assignedAssets.confirmations.delete.message', { compliance: this.complianceName }),
      warningMessage: this.transloco.translate('compliance.library.assignedAssets.confirmations.delete.warning', { compliance: this.complianceName }),
      confirmationPrompt: 'delete',
      confirmPlaceholder: this.transloco.translate('compliance.library.assignedAssets.confirmations.delete.placeholder'),
      confirmText: this.transloco.translate('compliance.library.assignedAssets.confirmations.delete.confirm'),
      declineText: this.transloco.translate('compliance.library.assignedAssets.confirmations.delete.decline'),
      closeOnSuccess: false,
      onConfirm: async () => {
        try {
          await this.complianceLibraryService.deleteAssignedAssetAsync({
            assetId: item.id,
            complianceId: this.complianceId
          });
          this.toastr.success(this.transloco.translate('compliance.library.assignedAssets.toasts.removed', { name: item.name }));
          modalRef.close(true);
        } catch (error) {
          this.errorService.handleError(error, { showToast: true });
        }
      }
    };

    modalRef.closed.subscribe((shouldReload: boolean) => {
      if (shouldReload) {
        this.resetAndLoad();
      }
    });
  }

  public async loadAssignedAssetsAsync(resetResults: boolean = false): Promise<void> {
    this.hasError = false;
    const requestedPage = this.page;

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancellation.next();
      } else {
        this.isLoadingMore = true;
      }

      const request: GetAssignedAssetsRequest = {
        complianceId: this.complianceId,
        searchTerm: this.searchTerm,
        assetType: this.assetType,
        page: requestedPage,
        pageSize: this.pageSize
      };

      const response = await this.complianceLibraryService.getAssignedAssetsAsync(request, this.cancellation);

      const result = response.data;
      this.totalCount = result?.totalCount ?? 0;
      this.totalPages = result?.totalPages ?? 0;

      const assets = (result?.results ?? []).map(a => ({
        ...a,
        complianceFrequencyLabel: this.tableTaskType === 'routine'
          ? translateRoutineTypeName(this.transloco, a.complianceFrequency)
          : translateComplianceFrequency(this.transloco, a.complianceFrequency),
        status: a.isCompliant == null ? undefined : a.isCompliant ? 'Compliant' : 'Non-compliant'
      }));

      if (requestedPage === 1) {
        this.assignedAssets = assets;
      } else {
        this.assignedAssets = [...this.assignedAssets, ...assets];
      }

      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          if (requestedPage > 1) {
            this.page--;
          }

          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  private resetAndLoad(): void {
    this.page = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.assignedAssets = [];
    void this.loadAssignedAssetsAsync(true);
  }

  protected override onRefresh(): void { }
  protected override shouldRefresh(): boolean { return false; }
  protected override getFilters() { return {}; }
  protected override applyPageFilterState(pageFilter: any): void { }
}